// src/reducers/sancion_slice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../services/apiService";

// ====== Services (mismo estilo que habilitacionLocal_slice) ======
const sancionService = apiService("/sanciones"); // => /api/sanciones
const API_BASE = process.env.REACT_APP_API_URL || "/api";

/* ===================== THUNKS ===================== */

/**
 * Lista general de sanciones con query opcional:
 * { id_afiliado, id_usuario, desde, hasta, limit, offset, order, ... }
 */
export const fetchSanciones = createAsyncThunk(
  "sancion/fetchSanciones",
  async (query = {}, { rejectWithValue }) => {
    try {
      const qs = toQueryString(query);
      // apiService expone getAllSome, se puede pasar `?param=...`
      const resp = await sancionService.getAllSome(qs ? `?${qs}` : "");
      // esperado: { count, rows } | [] => normalizamos
      return Array.isArray(resp) ? { count: resp.length, rows: resp } : (resp || { count: 0, rows: [] });
    } catch (error) {
      return rejectWithValue(error?.response?.data || { error: error.message });
    }
  }
);

/** Obtener una sanción por ID */
export const fetchSancionById = createAsyncThunk(
  "sancion/fetchSancionById",
  async (id, { rejectWithValue }) => {
    try {
      const resp = await sancionService.getById(id);
      return resp;
    } catch (error) {
      return rejectWithValue(error?.response?.data || { error: error.message });
    }
  }
);

/**
 * Crear sanción (con archivo PDF/JPG opcional).
 * Espera un FormData con:
 * - id_afiliado, id_usuario, sancion_estado ('CON SANCION'|'SIN SANCION'),
 * - fecha_imposicion, fecha_inicio, fecha_fin?,
 * - tipo_sancion, observacion?,
 * - resolucion (file) opcional
 */
export const addSancion = createAsyncThunk(
  "sancion/addSancion",
  async (formData, { rejectWithValue }) => {
    try {
      // multipart: usa createWithFormDataByEndpoint (como en tu apiService)
      const resp = await sancionService.createWithFormDataByEndpoint("", formData);
      return resp; // { message, sancion, afiliado } o el objeto creado
    } catch (error) {
      return rejectWithValue(error?.response?.data || { error: error.message });
    }
  }
);

/**
 * Actualizar sanción (con o sin archivo).
 * data puede ser FormData o un objeto plano.
 */
export const modifySancion = createAsyncThunk(
  "sancion/modifySancion",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
      // apiService ya tiene updateWithFormData (PUT /:id con multipart)
      const resp = isFormData ? await sancionService.updateWithFormData(id, data)
                              : await sancionService.update(id, data);
      return resp;
    } catch (error) {
      return rejectWithValue(error?.response?.data || { error: error.message });
    }
  }
);

/** Eliminar sanción */
export const removeSancion = createAsyncThunk(
  "sancion/removeSancion",
  async (id, { rejectWithValue }) => {
    try {
      await sancionService.remove(id);
      return id;
    } catch (error) {
      return rejectWithValue(error?.response?.data || { error: error.message });
    }
  }
);

/**
 * Cambiar estado de sanción del afiliado (PATCH):
 * PATCH /api/sanciones/afiliados/:id/estado  { sancion_estado: 'CON SANCION'|'SIN SANCION' }
 * Se usa fetch directo (igual que en habilitacionLocal_slice para PATCH).
 */
export const setAfiliadoSancionEstado = createAsyncThunk(
  "sancion/setAfiliadoSancionEstado",
  async ({ idAfiliado, sancion_estado }, { rejectWithValue }) => {
    try {
      const url = `${API_BASE}/sanciones/afiliados/${idAfiliado}/estado`;
      const token = localStorage.getItem("token");
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ sancion_estado }),
      });
      if (!res.ok) {
        const data = await safeJson(res);
        throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      }
      const data = await safeJson(res);
      return data; // { message, afiliado } o similar
    } catch (error) {
      return rejectWithValue(error?.message || "Error al cambiar estado de sanción");
    }
  }
);

/**
 * Historial de sanciones de un afiliado
 */
export const fetchSancionesByAfiliado = createAsyncThunk(
  "sancion/fetchSancionesByAfiliado",
  async (idAfiliado, { rejectWithValue }) => {
    try {
      const qs = toQueryString({
        id_afiliado: idAfiliado,
        order: "createdAt:DESC",
        limit: 200,
        offset: 0,
      });
      const resp = await sancionService.getAllSome(qs ? `?${qs}` : "");
      const normalized = Array.isArray(resp) ? { count: resp.length, rows: resp } : (resp || { count: 0, rows: [] });
      return { idAfiliado, data: normalized };
    } catch (error) {
      return rejectWithValue(error?.response?.data || { error: error.message, idAfiliado });
    }
  }
);

/* ===================== SLICE ===================== */

const initialState = {
  sanciones: [],     // lista general
  count: 0,          // total general
  current: null,     // sanción seleccionada
  byAfiliado: {},    // cache: { [idAfiliado]: { count, rows } }
  status: "idle",
  error: null,
};

const sancionSlice = createSlice({
  name: "sancion",
  initialState,
  reducers: {
    setCurrentSancion(state, action) {
      state.current = action.payload || null;
    },
    clearSancionesCacheByAfiliado(state, action) {
      const idAf = action.payload;
      if (idAf && state.byAfiliado[idAf]) {
        delete state.byAfiliado[idAf];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // LISTA GENERAL
      .addCase(fetchSanciones.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchSanciones.fulfilled, (state, action) => {
        state.status = "idle";
        state.count = action.payload?.count || 0;
        state.sanciones = action.payload?.rows || [];
      })
      .addCase(fetchSanciones.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.error || action.error?.message;
      })

      // GET BY ID
      .addCase(fetchSancionById.fulfilled, (state, action) => {
        state.current = action.payload || null;
      })

      // CREATE
      .addCase(addSancion.fulfilled, (state, action) => {
        const sancion = action.payload?.sancion || action.payload;
        if (sancion) {
          state.sanciones.unshift(sancion);
          state.count += 1;
          const idAf = sancion.id_afiliado;
          if (idAf) {
            const prev = state.byAfiliado[idAf] || { count: 0, rows: [] };
            state.byAfiliado[idAf] = {
              count: (prev.count || 0) + 1,
              rows: [sancion, ...(prev.rows || [])],
            };
          }
        }
        state.error = null;
      })
      .addCase(addSancion.rejected, (state, action) => {
        state.error = action.payload?.error || action.error?.message;
      })

      // UPDATE
      .addCase(modifySancion.fulfilled, (state, action) => {
        const upd = action.payload;
        if (!upd) return;
        const idx = state.sanciones.findIndex((x) => x.id === upd.id);
        if (idx !== -1) state.sanciones[idx] = upd;

        const idAf = upd.id_afiliado;
        if (idAf && state.byAfiliado[idAf]?.rows) {
          const rows = state.byAfiliado[idAf].rows;
          const i2 = rows.findIndex((x) => x.id === upd.id);
          if (i2 !== -1) rows[i2] = upd;
        }
        if (state.current?.id === upd.id) state.current = upd;
        state.error = null;
      })
      .addCase(modifySancion.rejected, (state, action) => {
        state.error = action.payload?.error || action.error?.message;
      })

      // REMOVE
      .addCase(removeSancion.fulfilled, (state, action) => {
        const id = action.payload;
        state.sanciones = state.sanciones.filter((x) => x.id !== id);
        state.count = Math.max(0, state.count - 1);

        Object.keys(state.byAfiliado).forEach((k) => {
          const bucket = state.byAfiliado[k];
          if (!bucket?.rows) return;
          const before = bucket.rows.length;
          bucket.rows = bucket.rows.filter((x) => x.id !== id);
          if (bucket.rows.length !== before) {
            bucket.count = Math.max(0, (bucket.count || before) - 1);
          }
        });

        if (state.current?.id === id) state.current = null;
        state.error = null;
      })
      .addCase(removeSancion.rejected, (state, action) => {
        state.error = action.payload?.error || action.error?.message;
      })

      // PATCH estado afiliado
      .addCase(setAfiliadoSancionEstado.fulfilled, (state) => {
        // No tocamos listas de sanciones aquí (esto solo cambia afiliado.sancion_estado)
        state.error = null;
      })
      .addCase(setAfiliadoSancionEstado.rejected, (state, action) => {
        state.error = action.payload || action.error?.message;
      })

      // HISTORIAL POR AFILIADO
      .addCase(fetchSancionesByAfiliado.fulfilled, (state, action) => {
        const { idAfiliado, data } = action.payload || {};
        if (idAfiliado) {
          state.byAfiliado[idAfiliado] = data || { count: 0, rows: [] };
        }
      })
      .addCase(fetchSancionesByAfiliado.rejected, (state, action) => {
        state.error = action.payload?.error || action.error?.message;
      });
  },
});

export const { setCurrentSancion, clearSancionesCacheByAfiliado } = sancionSlice.actions;
export default sancionSlice.reducer;

/* ===================== HELPERS ===================== */
function toQueryString(obj = {}) {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    params.append(k, String(v));
  });
  return params.toString();
}
async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
